import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { API } from '@/App';
import axios from 'axios';
import { Check } from 'lucide-react';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?active_only=true`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>Hizmetlerimiz - Ener Yazılım | AI, Siber Güvenlik, Web & Mobil Geliştirme</title>
        <meta name="description" content="AI geliştirme, siber güvenlik, web ve mobil uygulama geliştirme, bulut hizmetleri ve DevOps çözümleri ile işletmenizi dijital dönüşüme hazırlıyoruz." />
        <meta name="keywords" content="AI geliştirme, siber güvenlik, web geliştirme, mobil uygulama, bulut hizmetleri, devops, yazılım hizmetleri" />
        <link rel="canonical" href="https://www.eneryazilim.com/hizmetler" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eneryazilim.com/hizmetler" />
        <meta property="og:title" content="Hizmetlerimiz - Ener Yazılım" />
        <meta property="og:description" content="AI geliştirme, siber güvenlik, web ve mobil uygulama geliştirme, bulut hizmetleri ve DevOps çözümleri." />
        <meta property="og:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.eneryazilim.com/hizmetler" />
        <meta name="twitter:title" content="Hizmetlerimiz - Ener Yazılım" />
        <meta name="twitter:description" content="AI geliştirme, siber güvenlik, web ve mobil uygulama geliştirme, bulut hizmetleri ve DevOps çözümleri." />
        <meta name="twitter:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Structured Data - Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Technology Services",
            "provider": {
              "@type": "Organization",
              "name": "Ener Yazılım",
              "url": "https://www.eneryazilim.com"
            },
            "areaServed": "TR",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Teknoloji Hizmetleri",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "AI Geliştirme"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Siber Güvenlik"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Web & Mobil Geliştirme"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Bulut & DevOps"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#F7931E]/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="services-title">
              <span className="gradient-text">Hizmetlerimiz</span>
            </h1>
            <p className="text-lg text-white/70">
              Size özel çözümlerle dijital dönüşüm yolculuğunuzda yanınızdayız
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="pb-20" data-testid="services-list">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center text-white/60">Yükleniyor...</div>
          ) : services.length === 0 ? (
            <div className="text-center text-white/60" data-testid="no-services">Henüz hizmet eklenmemiş.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  data-testid={`service-${service.id}`}
                  className="glass p-8 rounded-2xl card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                  <p className="text-white/70 mb-6">{service.description}</p>
                  {service.features && service.features.length > 0 && (
                    <ul className="space-y-3">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <Check className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-white/60">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;